import type { SortItem } from '@/components/templates/Datatable/DataTableTypes'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'

type Item = {
	page: number
	items: any[]
}

type Collection = {
	name: string
	items: Item[]
	params: Api.IndexParams & Record<string, any>
	sortBy?: SortItem[]

	meta: Api.CollectionMeta
	links: Api.CollectionLinks

	reload: boolean
}

export const useCollectionStore = defineStore('collection', () => {
	const items = ref<Collection[]>([])

	function get(collectionName: string) {
		return items.value.find((item) => item.name === collectionName)
	}
	function getItems(collectionName: string) {
		return get(collectionName)?.items.flatMap((item) => item.items)
	}
	function getPage(collectionName: string, page: number) {
		return get(collectionName)?.items.find((item) => item.page === page)?.items
	}

	function pageExists(name: string, page: number) {
		const index = getCollectionIndex(name)
		if (index === undefined) return false
		return items.value[index].items
			?.flatMap((item) => item.page)
			?.includes(page)
	}

	function getCollectionIndex(name: string) {
		const val = items.value.findIndex((item) => item.name === name)
		return val === -1 ? undefined : val
	}
	function getItemIndex(name: string, page: number) {
		const collectionIndex = getCollectionIndex(name)
		if (collectionIndex === undefined) return undefined
		const val = items.value[collectionIndex].items.findIndex(
			(item) => item.page === page
		)
		return val === -1 ? undefined : val
	}

	function addOrUpdateItem(name: string, data?: Item) {
		if (!data) return
		const collectionIndex = getCollectionIndex(name)
		if (collectionIndex === undefined) return
		const itemIndex = getItemIndex(name, data.page)

		if (itemIndex !== undefined) {
			items.value[collectionIndex].items[itemIndex] = data
		} else {
			items.value[collectionIndex].items.push(data)
		}
	}

	function addOrUpdate(
		data: Omit<Collection, 'items'> & {
			item?: Item
		},
		options?: {}
	) {
		removeCollectionIfFirstPageChanged(data)

		const collectionIndex = getCollectionIndex(data.name)

		const { item, ...rest } = data

		if (collectionIndex !== undefined) {
			const collection = items.value[collectionIndex]
			items.value[collectionIndex] = { ...collection, ...rest }
		} else {
			items.value.push({ ...rest, items: [] })
		}

		addOrUpdateItem(data.name, item)
	}

	function removeCollection(name: string) {
		const index = getCollectionIndex(name)
		if (index === undefined) return
		items.value.splice(index, 1)
	}

	function removeCollectionIfFirstPageChanged({
		name,
		links,
	}: Omit<Collection, 'items'>) {
		const index = getCollectionIndex(name)
		if (index !== undefined && items.value[index].links.first !== links.first) {
			removeCollection(name)
		}
	}

	function $reset() {
		items.value = []
	}

	function setReload(collectionName: string, value: boolean) {
		const index = getCollectionIndex(collectionName)
		if (index === undefined) return
		items.value[index].reload = value
	}

	return {
		items,
		get,
		getItems,
		getPage,
		getCollectionIndex,
		getItemIndex,
		setReload,
		pageExists,
		addOrUpdate,
		addOrUpdateItem,
		removeCollection,
		$reset,
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useCollectionStore, import.meta.hot))
}
